import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { LoginRequest } from '../types/auth.types';

// Interface for LoginForm props
interface LoginFormProps {
  onSwitchToPasswordReset?: (email: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToPasswordReset }) => {
  const { login, loading, error, clearAuthError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    clearAuthError();
  }, [clearAuthError]);

  const validateForm = () => {
    const errors = { email: '', password: '' };
    let isValid = true;

    if (!formData.email) {
      errors.email = 'Email обов\'язковий';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Невірний формат email';
      isValid = false;
    }

    if (!formData.password) {
      errors.password = 'Пароль обов\'язковий';
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'Пароль повинен містити мінімум 6 символів';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const loginData: LoginRequest = {
      email: formData.email,
      password: formData.password,
    };

    await login(loginData);
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) {
      clearAuthError();
    }

    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));

    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handlePasswordResetClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onSwitchToPasswordReset) {
      onSwitchToPasswordReset(formData.email);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Вхід в систему
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', maxWidth: 400 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          autoComplete="email"
          autoFocus
          error={!!formErrors.email}
          helperText={formErrors.email}
          InputLabelProps={{ shrink: true }} // Додано для вирішення проблеми з автозаповненням
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          autoComplete="current-password"
          error={!!formErrors.password}
          helperText={formErrors.password}
          InputLabelProps={{ shrink: true }} // Додано для вирішення проблеми з автозаповненням
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Увійти'}
        </Button>

        <Box textAlign="center">
          <Typography variant="body2">
            Забули пароль?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={handlePasswordResetClick}
              sx={{ cursor: 'pointer' }}
            >
              Відновити
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
